import { clearTokens, getAuthConfigFromStorage, getTokens } from '../utils/authStorage';
import store from '../redux/store';
import { refreshAuthToken } from '../redux/reducers/authInfoReducer';
import { AuthConfig } from '../models/auth/AuthConfig';
import { AuthToken } from '../models/auth/AuthToken';

/**
 * OAuth2 client ID
 */
export const clientId = 'ui_client';

/**
 * OAuth2 client secret
 */
export const clientSecret = '$2a$05$5hcbpVj05z5JMXn9rQqCkO0yRiJbbG4Rd03oJOvdw727ckihX2TRi';

/**
 * URI для редиректа после успешной авторизации
 */
const redirectUri = window.location.origin + '/callback';

/**
 * Относительный путь до точки входа логина
 */
const loginUrl = 'oauth2/authorize';

/**
 * Статичная конфигурация
 */
export const environment = {
  production: false,
  login: `/${loginUrl}?response_type=code&client_id=${clientId}&scope=openid&redirect_uri=${redirectUri}`,
  logout: `/logout?response_type=code&client_id=${clientId}&scope=openid&redirect_uri=${redirectUri}`,
};

/**
 * Возвращает конфигурацию приложения.
 * Бросает ошибку, если она ещё не установлена.
 *
 * @returns AppConfig
 */
export const getAppConfig = (): AuthConfig => {
  const config = getAuthConfigFromStorage();
  if (!config) throw new Error('AppConfig not initialized');
  return config;
};

/**
 * Флаг, указывающий, выполняется ли в текущий момент операция обновления токена.
 * Это предотвращает множественные параллельные запросы на refresh.
 */
let isRefreshing = false;

/**
 * Список функций-колбэков, ожидающих получения валидного access_token после refresh.
 */
let subscribers: Array<(token: string) => void> = [];

/**
 * Добавляет колбэк в очередь ожидания обновлённого access_token.
 *
 * @param cb Колбэк, вызываемый при получении нового access_token
 */
const subscribe = (cb: (token: string) => void) => subscribers.push(cb);

/**
 * Уведомляет всех подписчиков о получении нового access_token и очищает список подписчиков.
 *
 * @param token Новый access_token
 */
const notifySubscribers = (token: string) => {
  subscribers.forEach((cb) => cb(token));
  subscribers = [];
};

/**
 * Выполняет очистку токенов и перенаправляет пользователя на страницу логина.
 * Используется в случае отсутствия токенов или ошибки при обновлении.
 */
export const redirectToLogin = () => {
  clearTokens();
  const config = getAppConfig();
  let res = `${config.baseUrl}${environment.login}`;

  const state = new URLSearchParams(window.location.search).get('state');
  if (state) res += `&state=${state}`;

  window.location.href = res;
};

/**
 * Проверяет, валиден ли текущий access token.
 * Если токен отсутствует или его срок действия истёк — возвращает `false`.
 *
 * @param tokens Объект токенов для проверки.
 * @returns `true`, если access_token существует и ещё не истёк; иначе `false`.
 */
const checkAccessTokenValid = (tokens: AuthToken | null | undefined): boolean => {
  if (!tokens?.valid_until || !tokens.access_token) {
    return false;
  }

  const validUntilDate = new Date(tokens.valid_until);
  if (isNaN(validUntilDate.getTime())) {
    return false;
  }

  return Date.now() < validUntilDate.getTime();
};

/**
 * Вспомогательная функция для получения актуальных токенов
 */
const requireTokens = (): AuthToken => {
  const tokens = getTokens();
  if (!tokens?.access_token || !tokens?.refresh_token) {
    throw { response: { status: 401, message: 'No tokens available' } };
  }
  return tokens;
};

/**
 * Получает валидный access_token, выполняя refresh при необходимости.
 *
 * Поведение:
 * - Если токен валиден — возвращает его.
 * - Если истёк, но есть refresh_token — обновляет и возвращает новый.
 * - Если обновление уже идёт — подписывается и ждёт его завершения.
 * - Если обновление не удалось — редиректит на login.
 *
 * @returns Promise<string | null> access_token либо null, если авторизация невозможна
 */
export const getValidAccessToken = async (): Promise<string> => {
  const tokens = requireTokens();

  if (checkAccessTokenValid(tokens)) {
    return tokens.access_token;
  }

  if (isRefreshing) {
    return new Promise((resolve) => subscribe(resolve));
  }

  isRefreshing = true;

  try {
    const newAccessToken = await refreshToken();
    notifySubscribers(newAccessToken);
    return newAccessToken;
  } catch (err) {
    throw { response: { status: 401, message: 'refresh token expired' } };
  } finally {
    isRefreshing = false;
  }
};

/**
 * Пробует обновить access_token с использованием refresh_token.
 * Если успешно — возвращает новый access_token.
 * Если неудачно — кидает исключение.
 */
export const refreshToken = async (): Promise<string> => {
  const tokens = requireTokens();

  const action = await store.dispatch(refreshAuthToken(tokens.refresh_token));

  if (refreshAuthToken.fulfilled.match(action)) {
    const newTokens = requireTokens();
    if (newTokens?.access_token) {
      return newTokens.access_token;
    }
  }

  throw new Error('Unable to refresh access token');
};
